/**
 * Hostname routing without listing domains: infer apex vs subdomain from DNS labels.
 * - Public hosts: two labels (e.g. `example.org`) → apex; three or more → first label = subdomain.
 * - Bare `localhost` / loopback IPs → apex so the marketing `Root` matches local dev (`http://localhost:3000/`).
 * - `anything.localhost` / `a.b.localhost` use the first label as subdomain (e.g. `test.localhost:3000`).
 * Does not use the public suffix list; multi-part registrable domains (e.g. `x.co.uk`) can be
 * misclassified as having a subdomain.
 */

/**
 * @param {string} [hostname] — defaults to `window.location.hostname`
 * @returns {{ subdomain: string | null, isApex: boolean }}
 */
export function parseHostname(hostname = typeof window !== 'undefined' ? window.location.hostname : '') {
  const h = String(hostname).toLowerCase();

  if (!h) {
    return { subdomain: null, isApex: false };
  }

  // Marketing root on local dev (not `foo.localhost` — that has multiple labels).
  if (
    h === 'localhost' ||
    h === '127.0.0.1' ||
    h === '::1' ||
    h === '[::1]'
  ) {
    return { subdomain: null, isApex: true };
  }

  const labels = h.split('.').filter(Boolean);

  const last = labels[labels.length - 1];
  if (last === 'localhost') {
    return { subdomain: labels[0], isApex: false };
  }

  if (labels.length === 2) {
    return { subdomain: null, isApex: true };
  }
  if (labels.length >= 3) {
    return { subdomain: labels[0], isApex: false };
  }
  return { subdomain: null, isApex: false };
}
